import { Fragment, useRef, type ReactNode } from "react";
import { Dialog, Transition } from "@headlessui/react";

export interface EmptyModalState {
  childComponent: ReactNode;
  open: boolean;
}

interface EmptyModalProps {
  childComponent: ReactNode;
  open: boolean;
  setOpen: (state: { childComponent: null; open: boolean }) => void;
  dismissible?: boolean;
}

export default function EmptyModal(props: EmptyModalProps) {
  const { childComponent, open, setOpen, dismissible = true } = props;

  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="hide-for-print relative z-10"
        initialFocus={cancelButtonRef}
        onClose={() => {
          if (dismissible) {
            setOpen({
              childComponent: null,
              open: false,
            });
          }
        }}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto z-10">
          <div className="flex mt-[50%] sm:mt-0 sm:min-h-full items-end justify-center p-4 sm:items-center sm:p-0 z-10">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 w-full sm:max-w-4xl sm:p-6 z-10 sm:max-h-[90vh] overflow-y-auto">
                {childComponent}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
